import { ReactNode, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { useMemberAuth } from '@/contexts/MemberAuthContext';

const tabs = [
  { label: '會員資料', path: '/account', icon: 'ri-user-settings-line' },
  { label: '訂單查詢', path: '/account/orders', icon: 'ri-file-list-3-line' },
  { label: '預約紀錄', path: '/account/reservations', icon: 'ri-calendar-check-line' },
  { label: '客服紀錄', path: '/account/support', icon: 'ri-customer-service-2-line' },
];

export default function AccountLayout({ children, title }: { children: ReactNode; title: string }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { member, isLoggedIn, loading, logout } = useMemberAuth();

  useEffect(() => {
    if (!loading && !isLoggedIn) {
      navigate(`/login?redirect=${encodeURIComponent(location.pathname)}`, { replace: true });
    }
  }, [loading, isLoggedIn, navigate, location.pathname]);

  async function handleLogout() {
    await logout();
    navigate('/');
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <main className="flex min-h-[60vh] items-center justify-center pt-20 md:pt-24">
          <i className="ri-loader-4-line animate-spin text-3xl text-[#1B3D2A]" />
        </main>
        <Footer />
      </div>
    );
  }

  if (!isLoggedIn) return null;

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main className="pt-20 md:pt-24">
        <div className="border-b border-[#E8E0D6] bg-[#FAF8F5] px-6 py-8 md:px-10 md:py-10 lg:px-16">
          <div className="mx-auto max-w-6xl">
            <h1 className="text-2xl font-bold text-[#1B3D2A] md:text-3xl">會員中心</h1>
            <p className="mt-1 text-sm text-[#5A6B5A]">{member?.Name} 您好，歡迎回來。</p>
          </div>
        </div>

        {/* 手機／平板：頁籤固定在頂部，內容區可獨立上下捲動 */}
        <div className="sticky top-14 z-20 border-b border-[#E8E0D6] bg-white/95 px-6 backdrop-blur md:top-20 lg:hidden">
          <div className="mx-auto flex max-w-6xl gap-2 overflow-x-auto py-3">
            {tabs.map((tab) => {
              const active = location.pathname === tab.path;
              return (
                <button
                  key={tab.path}
                  type="button"
                  onClick={() => navigate(tab.path)}
                  className={`flex shrink-0 items-center gap-1.5 rounded-full px-4 py-2 text-sm font-bold transition whitespace-nowrap ${
                    active ? 'bg-[#1B3D2A] text-white' : 'bg-[#FAF8F5] text-[#5A6B5A] hover:bg-[#1B3D2A]/8 hover:text-[#1B3D2A]'
                  }`}
                >
                  <i className={tab.icon} />
                  {tab.label}
                </button>
              );
            })}
            <button
              type="button"
              onClick={handleLogout}
              className="flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-4 py-2 text-sm font-bold text-[#c3423f] hover:bg-[#fdebea]"
            >
              <i className="ri-logout-box-line" />
              登出
            </button>
          </div>
        </div>

        <div className="mx-auto max-w-6xl px-6 py-8 md:px-10 md:py-12 lg:px-16">
          <div className="grid gap-8 lg:grid-cols-[240px_1fr] lg:items-start">
            {/* 桌面：側邊選單固定在畫面上，內容捲動時選單維持可見 */}
            <aside className="sticky top-24 hidden self-start rounded-2xl border border-[#E8E0D6] bg-white p-3 lg:block">
              <nav className="grid gap-1">
                {tabs.map((tab) => {
                  const active = location.pathname === tab.path;
                  return (
                    <button
                      key={tab.path}
                      type="button"
                      onClick={() => navigate(tab.path)}
                      className={`flex items-center gap-3 rounded-xl px-4 py-3 text-sm font-bold transition ${
                        active ? 'bg-[#1B3D2A] text-white' : 'text-[#5A6B5A] hover:bg-[#FAF8F5] hover:text-[#1B3D2A]'
                      }`}
                    >
                      <i className={`${tab.icon} text-base`} />
                      {tab.label}
                      {active && <i className="ri-arrow-right-s-line ml-auto" />}
                    </button>
                  );
                })}
              </nav>
              <div className="my-2 h-px bg-[#E8E0D6]" />
              <button
                type="button"
                onClick={handleLogout}
                className="flex w-full items-center gap-3 rounded-xl px-4 py-3 text-sm font-bold text-[#c3423f] transition hover:bg-[#fdebea]"
              >
                <i className="ri-logout-box-line text-base" />
                登出
              </button>
            </aside>

            <div className="min-w-0">
              <h2 className="mb-4 text-lg font-bold text-[#1B3D2A]">{title}</h2>
              {children}
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
